import { isArray, isEmpty } from 'lodash-es';
import queryString from 'query-string';

// getQueryParam, mergeQueryParam, stripQueryParam

/**
 * Appends query params to the given url.
 *
 * @param url - The base url.
 * @param params - The params to append.
 * @return - The url, with query params attached.
 */
export function appendQueryParams(url: string, params?: object): string {
  if (!params || isEmpty(params)) {
    return url;
  }

  const query = queryString.stringify(params);
  return query ? `${url}?${query}` : url;
}

type QueryOptions = {
  index?: number;
};

/**
 * Get decoded query param from url search string, like `?foo=1`
 *
 * @param search - The query search string (location.search).
 * @param propName - The propName within search.
 * @param index - Optional index for the query prop, if an array. Default 0
 * @return - The matching param, if found.
 */
export function getQueryParam(
  search: string,
  propName: string,
  options: QueryOptions = {}
): string | undefined {
  const { index = 0 } = options;
  const params = queryString.parse(search);
  const props = params[propName];
  if (props == null) {
    return undefined;
  }
  return isArray(props) ? (props[index] ?? undefined) : (props as string);
}

/**
 * Get decoded query params array from url search string, like `?foo=1&foo=2`
 *
 * @param search - The query search string (location.search).
 * @param propName - The propName within search.
 * @return - The matching params array, if found, else empty array
 */
export function getQueryParams<T extends string>(search: string, propName: string): T[] {
  const params = queryString.parse(search);
  const props = params[propName];
  if (props == null) {
    return [];
  }
  return isArray(props) ? (props as T[]) : [props as T];
}

// FIXME: Evaluate whether we should switch to an allow list, to reduce scope and have clearly defined allowances.
// https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html
const UNSAFE_REDIRECT_LIST = [
  // Anything looking like a URL, like `http://evil.org` or even ``//evil.org`
  /\/\//,
  // Catchall for http
  /^http/,
  // Scripts
  /javascript/,
  /data:/,
  /<.*script:/,
  /[\r\n]/,
  // See https://getcolor.atlassian.net/browse/VUL-47
  // Case: http://localhost:8888/sign-in?next=\/evil.org  `\evil.org` (with the leading backslash)
  /\\/,
];
/**
 * Return safe URL, while allowing URLs that are color.com
 */
export function getSafeUrl(nextUrl: string): string | undefined {
  // Check allowed redirect links.
  if (/^http.?:\/\/(home|www|providers)?\.?color.com/.test(nextUrl)) {
    return nextUrl;
  }
  // Deny anything that is unsafe, such as urls containing `//`, javascript, etc.
  if (UNSAFE_REDIRECT_LIST.some((regex) => regex.test(nextUrl))) {
    return undefined;
  }
  return nextUrl;
}
/**
 * Return safe URL from query param matching `next=` search query, while allowing URLs that are color.com
 */
export function getSafeNextQueryParam(search: string): string | undefined {
  const nextUrl = getQueryParam(search, 'next');
  if (!nextUrl) {
    return undefined;
  }

  // We do this to make sure to correctly encode the params in the next url if it exists and
  // this should be safe to do so as we don't usually have any other params when next is specified
  const additionalParams = queryString.parse(search.split(nextUrl)[1] ?? '');

  return getSafeUrl(appendQueryParams(nextUrl, additionalParams));
}

/**
 * Return safe URL from query param matching `verifyEmailNextUrl=` search query, while allowing URLs that are color.com
 */
export function getSafeVerifyEmailNextUrlQueryParam(search: string): string | undefined {
  const verifyEmailNextUrl = getQueryParam(search, 'verifyEmailNextUrl');
  if (!verifyEmailNextUrl) {
    return undefined;
  }

  // We do this to make sure to correctly encode the params in the next url if it exists and
  // this should be safe to do so as we don't usually have any other params when next is specified
  const additionalParams = queryString.parse(search.split(verifyEmailNextUrl)[1] ?? '');

  return getSafeUrl(appendQueryParams(verifyEmailNextUrl, additionalParams));
}

/**
 * Simple wrapper to generate a query string from key value pairs.
 *
 * @param params - key-value pairs. e.g.: { param: 'value' }
 * @return - The query search string (with `?` prefix)
 */
export function toQueryString(params: { [key: string]: any }): string {
  return `?${queryString.stringify(params)}`;
}

/**
 * Delete query param from url search string. For example, given `?foo=1&bar=2&x=3`, delete `bar`
 * returns `?foo=1&x=3`
 *
 * @param search - The query search string (location.search).
 * @param propName - The propName within search to delete.
 * @return - The updated query search string (with `?` prefix)
 */
export function stripQueryParam(search: string, propName: string): string {
  const remainder = queryString.exclude(search, [propName]);
  return remainder.length > 0 ? remainder : '?';
}

/**
 * Merge multiple query params into a URL search string. Values in `paramsToMerge` overwrite values
 * in `search`.
 *
 * @param search - The query search string (location.search).
 * @param updatedParams - Object mapping param names to values.
 * @returns - The updated query search string (with `?` prefix)
 */
export function mergeQueryParams(search: string, updatedParams: { [key: string]: any }): string {
  return toQueryString({
    ...queryString.parse(search),
    ...updatedParams,
  });
}

/**
 * merge query param into url search string. Examples:
 * given `?foo=1&bar=2&x=3`, merge prop `bar` with value 5
 * returns `?bar=5&foo=1&x=3`,
 * given `?foo=1&x=3`, merge prop `bar` with value 5
 * returns `?bar=5&foo=1&x=3`,
 *
 * @param search - The query search string (location.search).
 * @param propName - The propName to add or update.
 * @param propValue - The propValue.
 * @return - The updated query search string (with `?` prefix)
 */
export function mergeQueryParam(search: string, propName: string, propValue: string): string {
  return mergeQueryParams(search, { [propName]: propValue });
}

/**
 * Return current pathname plus search.
 * Example: /covid/sign-up/start?partner=sfdph
 */
export function getCurrentPath(): string {
  const { pathname, search } = window.location;
  return `${pathname}${search}`;
}

/**
 * Return search string with an escaped path to `nextUrl` as the `next` query param.
 * `nextUrl` defaults to the current path.
 *
 * For example:
 *   getNextSearchString() when on /covid/sign-up/start?partner=sfdph
 *   getNextSearchString('/covid/sign-up/start?partner=sfdph') when on any URL
 * both return `?next=%2Fcovid%2Fsign-up%2Fstart%3Fpartner%3Dsfdph`
 */
export function getNextSearchString(nextUrl?: string): string {
  return mergeQueryParam('', 'next', nextUrl ?? getCurrentPath());
}

/**
 * Return pathname without a trailing slash, if it has one. `path` should be
 * free of any search params.
 */
export function trimTrailingSlash(path: string): string {
  return path.replace(/\/$/, '');
}
